import {
  CallDto,
  FilterGroupDto,
  NeedDto,
  SuggestedActionDto,
  SuggestedNeedDto,
  SuggestedScheduledEventDto,
  VoiceCallAuditDto,
} from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { callsRequests } from '../requests/calls.request';
import { useAppSelector } from '../store-hooks';
import { CallState } from '../types/callsTypes';
import { createApiThunk } from '../utils/api-thunk';
import { ActivitySelectorParams } from '../types/selector-types';
import { getGuideInteractions } from './docketSlice';
import { feedSlice, getFeedItems } from './feedSlice';
import {
  CallUpdatedTypes,
  CallSocketEventTypes,
  CallWSPayload,
} from '@act/shared/socket-events';

import { WebsocketEventActions } from '../types/websocket-types';
import { addToEffectsMiddleware } from '../utils/effects-middleware';
import { verifyUser } from './actingUserSlice';
import { resetRedux } from '../actions/shared-actions';
// Import shared thunk to avoid circular dependency
import { getCalls } from '../thunks/calls.thunks';
export { getCalls };
import { getUsersByIds } from '../thunks/users.thunks';
import { selectUsersMap } from './usersSlice';

// INITIAL STATE ===============================================================
const callsAdapter = createEntityAdapter<CallDto>();
const voiceCallReviewAdapter = createEntityAdapter<VoiceCallAuditDto>();

const initialState: CallState = {
  callPageIds: [],
  callPageTotalCount: 0,
  calls: callsAdapter.getInitialState({
    status: 'initial',
  }),
  voiceCallReviews: voiceCallReviewAdapter.getInitialState({
    status: 'initial',
  }),
  callFilterOptions: null,
  callFilterOptionStatus: 'initial',
  justInTimeCalls: {},
  suggestions: {},
  suggestionsLoadingStatus: {},
};

// THUNKS/ACTIONS ==============================================================
export const updateCallNoFollowUpNeeded = createApiThunk(
  callsRequests.updateCallNoFollowUpNeeded
);
export const getCallFilters = createApiThunk(callsRequests.getCallsFilters);
export const updateSubjectId = createApiThunk(callsRequests.updateSubjectId);

export const getCallReviewTasks = createApiThunk(
  callsRequests.getCallReviewTask
);
export const updateCallReview = createApiThunk(callsRequests.updateCallReview);

export const getActiveCalls = createApiThunk(callsRequests.getActiveCalls);

export const getSuggestions = createApiThunk(callsRequests.getSuggestions);
export const updateSubjectIdSuccess = createAction<CallDto>(
  'calls/updateSubjectIdSuccess'
);

export const suggestedNeedUpdated = createAction<{
  callId: number;
  tempNeedId: string;
  createdNeed: NeedDto;
}>('calls/suggestedNeedUpdated');

export const suggestedActionUpdated = createAction<SuggestedActionDto>(
  'calls/suggestedActionUpdated'
);

export const suggestedScheduledEventUpdated =
  createAction<SuggestedScheduledEventDto>(
    'calls/suggestedScheduledEventUpdated'
  );
// SLICE =======================================================================
export const callsSlice = createSlice({
  name: 'calls',
  initialState,
  reducers: {
    callUpdated: (
      state,
      action: PayloadAction<{ call: CallDto; originalCall: CallDto }>
    ) => {
      const { call } = action.payload;
      if (state.calls.entities[call.id]) {
        // Update the call with the new data
        state.calls.entities[call.id] = {
          ...state.calls.entities[call.id],
          ...call,
        };
      }
    },
    callConnectionUpdated: (
      state,
      action: PayloadAction<{ connection: any }>
    ) => {
      const { connection } = action.payload;
      if (connection.callId && state.calls.entities[connection.callId]) {
        const call = state.calls.entities[connection.callId];
        // Update the connections array if it exists
        if (call.connections) {
          const connectionIndex = call.connections.findIndex(
            c => c.id === connection.id
          );
          if (connectionIndex >= 0) {
            call.connections[connectionIndex] = {
              ...call.connections[connectionIndex],
              ...connection,
            };
          } else {
            call.connections.push({
              id: connection.id,
              callId: connection.callId,
              userId: connection.userId,
              sid: connection.sid,
              phoneNumber: connection.phoneNumber,
              status: connection.status,
              onHold: connection.onHold ?? false,
              connectedAt: connection.connectedAt,
              completedAt: connection.completedAt,
              isAdded: connection.isAdded ?? false,
            });
          }
        } else {
          call.connections = [
            {
              id: connection.id,
              callId: connection.callId,
              userId: connection.userId,
              sid: connection.sid,
              phoneNumber: connection.phoneNumber,
              status: connection.status,
              onHold: connection.onHold ?? false,
              connectedAt: connection.connectedAt,
              completedAt: connection.completedAt,
              isAdded: connection.isAdded ?? false,
            },
          ];
        }
        // Update originator and receiver if they match
        if (call.originator?.userId === connection.userId) {
          call.originator.onHold = connection.onHold ?? false;
        }
        if (call.receiver?.userId === connection.userId) {
          call.receiver.onHold = connection.onHold ?? false;
        }
      }
      // Also update just in time calls if present
      if (connection?.callId && state.justInTimeCalls[connection.callId]) {
        const justInTimeCall = state.justInTimeCalls[connection.callId];

        // Check if we need to remove the call because it's completed (timeEnded is set)
        if (justInTimeCall.timeEnded) {
          const { [connection.callId]: removed, ...remaining } =
            state.justInTimeCalls;
          state.justInTimeCalls = remaining;
        } else {
          // Update the connection
          if (justInTimeCall.connections) {
            const connectionIndex = justInTimeCall.connections.findIndex(
              c => c.id === connection.id
            );
            if (connectionIndex >= 0) {
              justInTimeCall.connections[connectionIndex] = {
                ...justInTimeCall.connections[connectionIndex],
                ...connection,
              };
            } else {
              justInTimeCall.connections.push({
                id: connection.id,
                callId: connection.callId,
                userId: connection.userId,
                sid: connection.sid,
                phoneNumber: connection.phoneNumber,
                status: connection.status,
                onHold: connection.onHold ?? false,
                connectedAt: connection.connectedAt,
                completedAt: connection.completedAt,
                isAdded: connection.isAdded ?? false,
              });
            }
          } else {
            justInTimeCall.connections = [
              {
                id: connection.id,
                callId: connection.callId,
                userId: connection.userId,
                sid: connection.sid,
                phoneNumber: connection.phoneNumber,
                status: connection.status,
                onHold: connection.onHold ?? false,
                connectedAt: connection.connectedAt,
                completedAt: connection.completedAt,
                isAdded: connection.isAdded ?? false,
              },
            ];
          }
        }
      }
    },
    callCreated: (state, action: PayloadAction<CallWSPayload>) => {
      const { call } = action.payload;
      state.justInTimeCalls[call.id] = call;
      // Also add to regular calls if not already present
      if (!state.calls.entities[call.id]) {
        callsAdapter.addOne(state.calls, call);
      }
    },
    justInTimeCallUpdated: (state, action: PayloadAction<CallWSPayload>) => {
      const { call } = action.payload;

      // If call is completed (timeEnded is set), remove it from justInTimeCalls
      if (call.timeEnded && state.justInTimeCalls[call.id]) {
        const { [call.id]: removed, ...remaining } = state.justInTimeCalls;
        state.justInTimeCalls = remaining;
      } else if (state.justInTimeCalls[call.id]) {
        // Only update if call is still active
        state.justInTimeCalls[call.id] = {
          ...state.justInTimeCalls[call.id],
          ...call,
        };
      }

      // Also update regular calls if present
      if (state.calls.entities[call.id]) {
        state.calls.entities[call.id] = {
          ...state.calls.entities[call.id],
          ...call,
        };
      }
    },
    removeJustInTimeCall: (state, action: PayloadAction<number>) => {
      const { [action.payload]: removed, ...remaining } = state.justInTimeCalls;
      state.justInTimeCalls = remaining;
    },
    suggestedActionsUpdated: (
      state,
      action: PayloadAction<{
        call: CallDto;
        isLlmSuggestionsProcessed: boolean;
        llmSuggestions: {
          needs: SuggestedNeedDto[];
          actions: SuggestedActionDto[];
          scheduledEvents: SuggestedScheduledEventDto[];
        };
      }>
    ) => {
      const callId = action.payload.call.id;
      const { isLlmSuggestionsProcessed, llmSuggestions } = action.payload;
      if (isLlmSuggestionsProcessed) {
        state.calls.entities[callId] = {
          ...state.calls.entities[callId],
          isLlmSuggestionsProcessed: isLlmSuggestionsProcessed,
        };
        if (llmSuggestions) {
          const filteredPayload = {
            ...llmSuggestions,
            actions: llmSuggestions?.actions?.filter(
              action => !(action?.tempNeedId && !action?.needId)
            ),
          };
          state.suggestions[callId] = filteredPayload;
        }
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getCalls.pending, state => {
        state.calls.status = 'loading';
      })
      .addCase(getCalls.fulfilled, (state, action) => {
        // Remove only the previously paginated calls to prevent store pollution
        // This preserves feed calls and other calls loaded from different sources
        if (state.callPageIds.length > 0) {
          callsAdapter.removeMany(state.calls, state.callPageIds);
        }

        // Add only the new filtered results
        callsAdapter.addMany(state.calls, action.payload.calls);
        state.callPageTotalCount = action.payload.count;
        state.callPageIds = action.payload.calls.map(call => call.id);
        state.calls.status = 'success';
      })
      .addCase(getCalls.rejected, state => {
        state.calls.status = 'failed';
      })
      .addCase(getFeedItems.fulfilled, (state, action) => {
        callsAdapter.addMany(state.calls, action.payload.calls);
        state.calls.status = 'success';
      })
      .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
        if (!action.payload.feedItemModelView.calls) return;

        callsAdapter.upsertMany(
          state.calls,
          action.payload.feedItemModelView.calls
        );
      })
      // Docket Interactions
      .addCase(getGuideInteractions.fulfilled, (state, action) => {
        callsAdapter.addMany(state.calls, action.payload.calls);
      })
      .addCase(getCallFilters.pending, state => {
        state.callFilterOptionStatus = 'loading';
      })
      .addCase(
        getCallFilters.fulfilled,
        (state, action: PayloadAction<FilterGroupDto>) => {
          state.callFilterOptionStatus = 'success';
          state.callFilterOptions = action.payload;
        }
      )
      .addCase(getCallFilters.rejected, state => {
        state.callFilterOptionStatus = 'failed';
      })
      .addCase(updateSubjectId.pending, state => {
        state.calls.status = 'loading';
      })
      .addCase(
        updateSubjectId.fulfilled,
        (state, action: PayloadAction<CallDto>) => {
          state.calls.status = 'success';
          if (state.calls.entities[action.payload.id]) {
            state.calls.entities[action.payload.id] = {
              ...state.calls.entities[action.payload.id],
              ...action.payload,
            };
          }
          // Also update justInTimeCalls if this call is there
          if (state.justInTimeCalls[action.payload.id]) {
            state.justInTimeCalls[action.payload.id] = {
              ...state.justInTimeCalls[action.payload.id],
              ...action.payload,
            };
          }
        }
      )
      .addCase(updateSubjectIdSuccess, (state, action) => {
        if (state.calls.entities[action.payload.id]) {
          state.calls.entities[action.payload.id] = {
            ...state.calls.entities[action.payload.id],
            ...action.payload,
          };
        }
        // Also update justInTimeCalls if this call is there
        if (state.justInTimeCalls[action.payload.id]) {
          state.justInTimeCalls[action.payload.id] = {
            ...state.justInTimeCalls[action.payload.id],
            ...action.payload,
          };
        }
        state.calls.status = 'success';
      })
      .addCase(updateSubjectId.rejected, state => {
        state.calls.status = 'failed';
      })
      .addCase(getCallReviewTasks.pending, state => {
        state.voiceCallReviews.status = 'loading';
      })
      .addCase(getCallReviewTasks.fulfilled, (state, action) => {
        voiceCallReviewAdapter.addMany(state.voiceCallReviews, action.payload);
        state.voiceCallReviews.status = 'success';
      })
      .addCase(getCallReviewTasks.rejected, state => {
        state.voiceCallReviews.status = 'failed';
      })
      .addCase(updateCallReview.fulfilled, (state, action) => {
        voiceCallReviewAdapter.upsertOne(
          state.voiceCallReviews,
          action.payload
        );
      })
      .addCase(getActiveCalls.fulfilled, (state, action) => {
        // Populate justInTimeCalls with active calls
        action.payload.forEach(call => {
          state.justInTimeCalls[call.id] = call;
        });
        // Also add to regular calls
        callsAdapter.upsertMany(state.calls, action.payload);
      })
      .addCase(getSuggestions.pending, (state, action) => {
        const callId = action.meta.arg?.params?.id;
        if (callId) {
          state.suggestionsLoadingStatus[callId] = 'loading';
        }
      })
      .addCase(getSuggestions.fulfilled, (state, action) => {
        const callId = action.meta.arg?.params?.id;
        if (callId) {
          const filteredPayload = {
            ...action.payload,
            actions: action.payload?.actions?.filter(
              action => !(action?.tempNeedId && !action?.needId)
            ),
          };
          state.suggestions[callId] = filteredPayload;
          state.suggestionsLoadingStatus[callId] = 'success';
        }
      })
      .addCase(getSuggestions.rejected, (state, action) => {
        const callId = action.meta.arg?.params?.id;
        if (callId) {
          state.suggestionsLoadingStatus[callId] = 'failed';
        }
      });
  },
});

// SELECTORS ===================================================================
export const selectCallSliceState = state =>
  (state as { calls: typeof initialState })?.calls;

const selectCallsState = createSelector(selectCallSliceState, s => s.calls);

export const { selectAll: selectCalls, selectEntities: selectCallsMap } =
  callsAdapter.getSelectors(selectCallsState);

export const selectCallById = (id: number) =>
  createSelector(selectCallsMap, callsMap => {
    return callsMap[id];
  });

export const selectCallFilters = createSelector(
  selectCallSliceState,
  s => s.callFilterOptions
);

export const selectCallFilterLoadingStatus = createSelector(
  selectCallSliceState,
  s => s.callFilterOptionStatus
);

const {
  selectById: selectVoiceCallAuditById,
  selectAll: selectVoiceCallReviews,
} = voiceCallReviewAdapter.getSelectors();

const selectCallsAreLoading = state => selectCallSliceState(state).calls.status;
const selectCallsReviewsAreLoading = state =>
  selectCallSliceState(state).voiceCallReviews.status;

const selectCallsPage = createSelector(selectCallSliceState, state =>
  state.callPageIds.map(id => state.calls.entities[id])
);

export const selectCallsPageTotal = state =>
  (state as { calls: typeof initialState })?.calls.callPageTotalCount;

export const selectCallsByGuideAndTime: (
  state,
  paramObj: ActivitySelectorParams
) => CallDto[] = createSelector(
  [
    selectCalls,
    (state, paramObj: ActivitySelectorParams) => paramObj.guideId,
    (state, paramObj: ActivitySelectorParams) => paramObj.startEpochTime,
    (state, paramObj: ActivitySelectorParams) => paramObj.endEpochTime,
  ],
  (calls, guideId, startEpochTime, endEpochTime) => {
    return calls.filter(
      c =>
        (c.originator?.userId === guideId || c.receiver?.userId === guideId) &&
        c.timestamp >= startEpochTime &&
        c.timestamp <= endEpochTime
    );
  }
);

// Voice Call Audit Selectors
const selectvoiceCallReviewsState = createSelector(
  selectCallSliceState,
  s => s.voiceCallReviews
);

export const selectVoiceCallReviewsByCallId = createSelector(
  [selectvoiceCallReviewsState, (state, callId: number) => callId],
  (auditsState, callId) => {
    const audits = Object.values(auditsState.entities || {});
    const filtered = audits.filter(
      audit => audit && Number(audit.callId) === callId
    );
    return filtered;
  }
);

export const selectVoiceCallReviewsByAssigneeId = createSelector(
  [selectvoiceCallReviewsState, (state, assigneeId: string) => assigneeId],
  (auditsState, assigneeId) => {
    const audits = Object.values(auditsState.entities || {});
    return audits.filter(audit => audit && audit.assigneeId === assigneeId);
  }
);

// Suggestions Selectors
export const selectSuggestionsByCallId = createSelector(
  [selectCallSliceState, (state, callId: number) => callId],
  (callState, callId) => callState.suggestions[callId] || null
);

export const selectSuggestionsLoadingStatusByCallId = createSelector(
  [selectCallSliceState, (state, callId: number) => callId],
  (callState, callId) => callState.suggestionsLoadingStatus[callId] || 'initial'
);

// REDUCER =====================================================================
export const callsReducer = callsSlice.reducer;

// EXPORT HELPER ===============================================================
export const callsAppSelectors = {
  useSelectCallById: id => useAppSelector(selectCallById(id)),
  useSelectAllCalls: () => useAppSelector(selectCalls),
  useSelectCallsPage: () => useAppSelector(selectCallsPage),
  useSelectCallsPageTotal: () => useAppSelector(selectCallsPageTotal),
  useSelectCallsFilters: () => useAppSelector(selectCallFilters),
  useSelectCallsFiltersLoadingStatus: () =>
    useAppSelector(selectCallFilterLoadingStatus),
  useSelectCallsAreLoading: () => useAppSelector(selectCallsAreLoading),
  useSelectCallsReviewsAreLoading: () =>
    useAppSelector(selectCallsReviewsAreLoading),
  useSelectVoiceCallReviewsByCallId: (callId: number) =>
    useAppSelector(state => selectVoiceCallReviewsByCallId(state, callId)),
  useSelectVoiceCallReviewsByAssigneeId: (assigneeId: string) =>
    useAppSelector(state =>
      selectVoiceCallReviewsByAssigneeId(state, assigneeId)
    ),
  useSelectVoiceCallAuditById: (id: number) =>
    useAppSelector(state =>
      selectVoiceCallAuditById(state.calls.voiceCallReviews, id)
    ),
  useSelectAllCallReviews: () =>
    useAppSelector(state =>
      selectVoiceCallReviews(selectvoiceCallReviewsState(state))
    ),
  useSelectSuggestionsByCallId: (callId: number) =>
    useAppSelector(state => selectSuggestionsByCallId(state, callId)),
  useSelectSuggestionsLoadingStatusByCallId: (callId: number) =>
    useAppSelector(state =>
      selectSuggestionsLoadingStatusByCallId(state, callId)
    ),
};

// SELECTORS FOR JUST IN TIME CALLS ============================================
export const selectJustInTimeCalls = createSelector(selectCallSliceState, s =>
  Object.values(s.justInTimeCalls)
);

// HELPER FUNCTIONS ============================================================
/**
 * Fetches missing users from calls if they're not already in the store
 * @param calls - Single call or array of calls
 * @param state - Redux state
 * @param dispatch - Redux dispatch function
 */
const fetchMissingUsersFromCalls = (
  calls: CallDto | CallDto[],
  state: any,
  dispatch: any
) => {
  const callsArray = Array.isArray(calls) ? calls : [calls];
  if (callsArray.length === 0) return;

  const usersMap = selectUsersMap(state);
  const missingUserIds = new Set<string>();

  // Collect all user IDs from calls
  callsArray.forEach(call => {
    // Check subjectId
    if (call.subjectId && !usersMap[call.subjectId]) {
      missingUserIds.add(call.subjectId);
    }

    // Check originator userId
    if (call.originator?.userId && !usersMap[call.originator.userId]) {
      missingUserIds.add(call.originator.userId);
    }

    // Check receiver userId
    if (call.receiver?.userId && !usersMap[call.receiver.userId]) {
      missingUserIds.add(call.receiver.userId);
    }

    // Check all connection userIds
    call.connections?.forEach(connection => {
      if (connection.userId && !usersMap[connection.userId]) {
        missingUserIds.add(connection.userId);
      }
    });
  });

  // Fetch missing users if any
  if (missingUserIds.size > 0) {
    void dispatch(
      getUsersByIds({ params: { ids: Array.from(missingUserIds) } })
    );
  }
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: verifyUser.fulfilled,
    effect: async ({ dispatch }) => {
      // Fetch active calls when user logs in
      dispatch(getActiveCalls({}));
    },
  });

  // Fetch missing users when active calls are loaded
  addEffect({
    action: getActiveCalls.fulfilled,
    effect: async ({ dispatch, action, state }) => {
      if (!action.payload || action.payload.length === 0) return;
      fetchMissingUsersFromCalls(action.payload, state, dispatch);
    },
  });

  // Fetch missing users when calls are updated via websocket
  addEffect({
    action: callsSlice.actions.justInTimeCallUpdated,
    effect: async ({ dispatch, action, state }) => {
      const call = action.payload?.call;
      if (!call) return;
      fetchMissingUsersFromCalls(call, state, dispatch);
    },
  });

  // Fetch missing users when new calls are created via websocket
  addEffect({
    action: callsSlice.actions.callCreated,
    effect: async ({ dispatch, action, state }) => {
      const call = action.payload?.call;
      if (!call) return;
      fetchMissingUsersFromCalls(call, state, dispatch);
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
export const callsSliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: CallUpdatedTypes.CALL_UPDATED,
    action: callsSlice.actions.callUpdated,
    channel: 'baseSocket',
  },
  {
    type: CallUpdatedTypes.CALL_CONNECTION_UPDATED,
    action: callsSlice.actions.callConnectionUpdated,
    channel: 'baseSocket',
  },
  {
    type: CallSocketEventTypes.INCOMING_CALL_CREATED,
    action: callsSlice.actions.callCreated,
    channel: 'baseSocket',
  },
  {
    type: CallSocketEventTypes.OUTGOING_CALL_CREATED,
    action: callsSlice.actions.callCreated,
    channel: 'baseSocket',
  },
  {
    type: CallSocketEventTypes.CALL_UPDATED,
    action: callsSlice.actions.justInTimeCallUpdated,
    channel: 'baseSocket',
  },
  {
    type: CallUpdatedTypes.SUGGESTED_ACTIONS_UPDATED,
    action: callsSlice.actions.suggestedActionsUpdated,
    channel: 'baseSocket',
  },
];
